import styles from "./index.module.scss";
import { useTranslations } from "next-intl";
import React, { useEffect, useState } from "react";
import { PaymentSystem, PaymentSystemFormDataType } from "@/domains/profile/types";
import Image from "next/image";
import getSymbolFromCurrency from "currency-symbol-map";
import { useForm, SubmitHandler } from "react-hook-form";
import { usePaymentForm } from "@/domains/profile/hooks/use-payment-form";
import { IMaskInput } from "react-imask";
import { useStore } from "@/hooks/use-store";

export default function DepositTab({
  paymentSystems,
  type,
}: {
  paymentSystems: PaymentSystem[];
  type: string;
}) {
  const tForm = useTranslations("DepositModalForm");
  const tValidation = useTranslations("Validation");
  const [selectedMethod, setSelectedMethod] = useState<PaymentSystem | null>(null);
  const [errorsPayment, setErrorsPayment] = useState<Record<string, string> | null>({});
  const { onMutatePayment, errors: paymentErrors, loading } = usePaymentForm();
  const { modal } = useStore();

  useEffect(() => {
    setErrorsPayment({});
    setSelectedMethod(null);
  }, [type]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<PaymentSystemFormDataType>({
    mode: "all",
  });

  const handleChange = (item: PaymentSystem | null) => {
    setSelectedMethod(item);
    reset();
  };

  const onSubmit: SubmitHandler<PaymentSystemFormDataType> = async (data) => {
    if (!selectedMethod) return;

    data.type = type;
    data.method = selectedMethod.id;

    if (data.amount !== undefined && data.amount <= 0) {
      return;
    }

    const result = await onMutatePayment(data);

    setErrorsPayment(paymentErrors);

    if (result.success) {
      reset();

      if (result.response?.url) {
        window.location.href = result.response.url;
      }

      modal.closeModal();
    }
  };

  return (
    <div>
      <div className={styles.depositGrid}>
        {paymentSystems.map((item) => (
          <button
            key={item.id}
            className={`${styles.depositMethodCard} ${item.id === selectedMethod?.id ? styles.active : ""}`}
            onClick={() => handleChange(item)}
          >
            <div className={styles.depositMethodLogo}>
              <Image
                width={100}
                height={50}
                src={`/images/payments/${item.id}.svg`}
                alt={item.id}
                className="h-full object-contain"
              />
            </div>
            <div className={styles.depositRange}>
              {item.min} - {item.max} {getSymbolFromCurrency(item.currency || "")}
            </div>
          </button>
        ))}
      </div>

      {selectedMethod && (
        <div className={styles.depositFormContainer}>
          <div className={styles.depositFormWarning}>{tForm("warning_info")}</div>

          <form
            className={styles.depositForm}
            onSubmit={(e) => {
              handleSubmit(onSubmit)(e);
            }}
          >
            <div className={styles.depositFormRange}>
              <span>
                {tForm("min")}: {selectedMethod.min}{" "}
              </span>
              <span> / </span>
              <span>
                {tForm("max")}: {selectedMethod.max}{" "}
                {getSymbolFromCurrency(selectedMethod.currency || "")}
              </span>
            </div>

            {type === "out" && (
              <div className={styles.depositFormField}>
                <label className={styles.label}>
                  <IMaskInput
                    {...register("receiverWallet", {
                      required: tValidation("required"),
                    })}
                    mask="0000 0000 0000 0000"
                    unmask
                    className={`${styles.input} ${errorsPayment?.receiverWallet ? styles.active : ""}`}
                    placeholder={""}
                    onAccept={(value) => setValue("receiverWallet", value)}
                  />

                  <span style={{ color: "white" }} className={styles.label_value}>
                    {tForm("card")}
                  </span>
                </label>
                {errorsPayment?.receiverWallet && (
                  <p className={styles.error}>
                    {tForm(`error_${errorsPayment.receiverWallet}`, {
                      min: selectedMethod.min,
                      max: selectedMethod.max,
                    })}
                  </p>
                )}
                {errors?.receiverWallet && (
                  <p className={styles.error}>{errors.receiverWallet.message}</p>
                )}
              </div>
            )}

            <div className={styles.depositFormField}>
              <label className={styles.label}>
                <IMaskInput
                  {...register("amount", {
                    required: tValidation("required"),
                  })}
                  mask={Number}
                  scale={0}
                  radix=","
                  mapToRadix={[".", ","]}
                  thousandsSeparator=" "
                  min={selectedMethod.min}
                  max={selectedMethod.max}
                  unmask
                  className={`${styles.input} ${errorsPayment?.amount ? styles.active : ""}`}
                  placeholder={""}
                  onAccept={(value) => setValue("amount", value as unknown as number)}
                />
                <span style={{ color: "white" }} className={styles.label_value}>
                  {tForm("amount")}
                </span>
              </label>
              {errorsPayment?.amount && (
                <p className={styles.error}>
                  {tForm(`error_${errorsPayment.amount}`, {
                    min: selectedMethod.min,
                    max: selectedMethod.max,
                  })}
                </p>
              )}
              {errors?.amount && <p className={styles.error}>{errors.amount.message}</p>}
            </div>

            <button className={styles.depositFormSubmit} type="submit" disabled={loading}>
              {loading ? tForm("submit_wait") : tForm("submit")}
            </button>
          </form>
        </div>
      )}

      {/* <form className={styles.form} onSubmit={handleFormSubmit}>
        <div className={styles.form__field}>
          <label className={`${styles.label} ${styles.phone_label}`}>123</label>
          <div className={styles.message}>123{renderErrorMessage("phone", authError || [])}</div>
          {errors.phoneInput &&
          <div className={styles.message}>{errors.phoneInput.message}</div>
          }
        </div>

        <div className={styles.form__field}>
          <button className={styles.btn_submit} type="submit" disabled={loading}>
            {loading ? "Отправка..." : "Получить пароль"}

          </button>
        </div>
      </form> */}
    </div>
  );
}
